import { ONE_TO_ONE_WHATSAPP_PHONE } from "@/lib/whatsapp";

export const INBOUND_SOURCE_CODES = {
  facebook: "SRC-FB",
  gbp: "SRC-GBP",
  instagram: "SRC-IG",
  lemon8: "SRC-L8",
  linkedin: "SRC-LI",
  newsletter: "SRC-EMAIL",
  pdf: "SRC-PDF",
  telegram: "SRC-TG",
  threads: "SRC-TH",
  tiktok: "SRC-TT",
  whatsapp: "SRC-WA",
  x: "SRC-X",
  xiaohongshu: "SRC-XHS",
  youtube: "SRC-YT",
} as const;

export type InboundSource = keyof typeof INBOUND_SOURCE_CODES;
export type InboundSourceCode = (typeof INBOUND_SOURCE_CODES)[InboundSource];

const INBOUND_SOURCE_CODE_VALUES = new Set<InboundSourceCode>(
  Object.values(INBOUND_SOURCE_CODES)
);

export const DIRECT_WHATSAPP_SOURCES = [
  "facebook",
  "gbp",
  "instagram",
  "lemon8",
  "linkedin",
  "threads",
  "tiktok",
  "x",
  "xiaohongshu",
  "youtube",
] as const satisfies readonly InboundSource[];

export type DirectWhatsAppSource = (typeof DIRECT_WHATSAPP_SOURCES)[number];

const SOCIAL_REFERRER_SOURCES: ReadonlyArray<{
  hostnames: readonly string[];
  source: InboundSource;
}> = [
  {
    hostnames: [
      "facebook.com",
      "l.facebook.com",
      "lm.facebook.com",
      "m.facebook.com",
    ],
    source: "facebook",
  },
  { hostnames: ["instagram.com", "l.instagram.com"], source: "instagram" },
  { hostnames: ["lemon8-app.com"], source: "lemon8" },
  { hostnames: ["linkedin.com", "lnkd.in"], source: "linkedin" },
  { hostnames: ["threads.com", "threads.net"], source: "threads" },
  { hostnames: ["tiktok.com", "vm.tiktok.com"], source: "tiktok" },
  { hostnames: ["t.co", "x.com"], source: "x" },
  {
    hostnames: ["xiaohongshu.com", "xhslink.com"],
    source: "xiaohongshu",
  },
  { hostnames: ["youtu.be", "youtube.com"], source: "youtube" },
];

function normalizeHostname(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^www\./, "");
}

export function normalizeInboundSource(value: unknown): InboundSource | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  return normalized in INBOUND_SOURCE_CODES
    ? (normalized as InboundSource)
    : null;
}

export function getInboundSourceCode(
  source: unknown
): InboundSourceCode | null {
  const normalized = normalizeInboundSource(source);
  return normalized ? INBOUND_SOURCE_CODES[normalized] : null;
}

export function isInboundSourceCode(
  value: unknown
): value is InboundSourceCode {
  return (
    typeof value === "string" &&
    INBOUND_SOURCE_CODE_VALUES.has(value as InboundSourceCode)
  );
}

export function sourceFromReferrer(referrer: string): InboundSource | null {
  if (!referrer.trim()) return null;

  try {
    const hostname = normalizeHostname(new URL(referrer).hostname);
    const match = SOCIAL_REFERRER_SOURCES.find(({ hostnames }) =>
      hostnames.some(
        (candidate) =>
          hostname === candidate || hostname.endsWith(`.${candidate}`)
      )
    );
    return match?.source ?? null;
  } catch {
    return null;
  }
}

export function appendSourceCode(
  message: string,
  sourceCode?: InboundSourceCode | null
): string {
  const trimmed = message.trim();
  if (!sourceCode || trimmed.includes(`Source: ${sourceCode}`)) return trimmed;
  return `${trimmed}\n\nSource: ${sourceCode}`;
}

export function buildSourceCodedWhatsAppDestination(
  source: DirectWhatsAppSource,
  message = "Hello Eclat Institute, I would like to enquire."
): string {
  const sourceCode = INBOUND_SOURCE_CODES[source];
  const codedMessage = appendSourceCode(message, sourceCode);
  return `https://wa.me/${ONE_TO_ONE_WHATSAPP_PHONE}?text=${encodeURIComponent(
    codedMessage
  )}`;
}

export function isDirectWhatsAppSource(
  value: unknown
): value is DirectWhatsAppSource {
  return (
    typeof value === "string" &&
    DIRECT_WHATSAPP_SOURCES.includes(value as DirectWhatsAppSource)
  );
}
